import { updateProfileInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { useAuthStore, useInvalidateMe } from '@/entities/user';
import { trpc } from '@/shared/api';
import { getErrorMessage, mapServerValidationErrors } from '@/shared/lib';

const FIELD_KEYS = ['name'] as const;

interface UseUpdateProfileResult {
  readonly nameError: string | null;
  readonly formError: string | null;
  readonly isPending: boolean;
  readonly isSaved: boolean;
  readonly submit: (name: string) => Promise<void>;
}

/**
 * 회원정보 변경(users.updateProfile) — 지금 계약은 이름 하나만 받는다.
 * 저장되면 헤더·인사가 읽는 세션 사용자(auth store)의 이름도 바꾸고 users.me 를 다시 읽는다.
 */
export const useUpdateProfile = (): UseUpdateProfileResult => {
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const mutation = trpc.users.updateProfile.useMutation();
  const invalidateMe = useInvalidateMe();

  const submit = async (name: string): Promise<void> => {
    setFormError(null);
    setIsSaved(false);

    const parsed = updateProfileInputSchema.safeParse({ name });

    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message ?? '이름을 확인해 주세요.');
      return;
    }

    setNameError(null);

    try {
      const account = await mutation.mutateAsync(parsed.data);
      const { user, setUser } = useAuthStore.getState();
      if (user !== null) setUser({ ...user, name: account.name });
      await invalidateMe();
      setIsSaved(true);
    } catch (error) {
      const serverErrors = mapServerValidationErrors(error, FIELD_KEYS);

      if (serverErrors) {
        setNameError(serverErrors.fieldErrors.name ?? null);
        setFormError(serverErrors.formError);
      } else {
        setFormError(getErrorMessage(error, '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.'));
      }
    }
  };

  return { nameError, formError, isPending: mutation.isPending, isSaved, submit };
};
